import * as Sentry from '@sentry/react';

import { GenericError } from 'support/helpers/errors/errors';
import { isDevelopment, isLocal } from 'support/helpers/generic/generic';

type LogErrorContext = {
  // React error boundary component stack (errorInfo.componentStack) — names the failing
  // component tree, which the raw minified production stack does not.
  componentStack?: string | null;
  extra?: Record<string, unknown>;
  tags?: Record<string, string>;
};

export const logError = (error: unknown, context?: LogErrorContext) => {
  if (isLocal() || isDevelopment()) {
    console.error(error);
  }

  Sentry.withScope((scope) => {
    if (error && typeof error === 'object') {
      // Handle regular error properties
      const errorObj = error as Record<string, unknown>;
      const excludedKeys = ['name', 'message', 'stack'];

      // Extract all properties except standard error properties
      const extras: Record<string, unknown> = {};

      // Use Object.getOwnPropertyNames to get all direct properties
      Object.getOwnPropertyNames(errorObj).forEach((key) => {
        if (!excludedKeys.includes(key)) {
          const value = errorObj[key];
          // Store value directly without stringification to avoid [object Object]
          extras[key] = value;
        }
      });

      // Special handling for GenericError
      if (error instanceof GenericError && error.otherInformation) {
        extras.otherInformation = error.otherInformation;
      }

      // Only set extras if we have any
      if (Object.keys(extras).length > 0) {
        scope.setExtras(extras);
      }
    }

    if (context?.componentStack) {
      scope.setContext('react', { componentStack: context.componentStack });
    }
    if (context?.extra) {
      scope.setExtras(context.extra);
    }
    if (context?.tags) {
      scope.setTags(context.tags);
    }

    Sentry.captureException(error);
  });
};
